import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Icosahedron,
  X,
  RefreshCw,
} from '@/components/ui/icons';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { ModelSelector } from '@/components/ModelSelector';
import { OutputFormatSelector } from '@/components/OutputFormatSelector';
import { useImageToCadModels } from '@/components/featureTreeSidebar/useImageToCadModels';
import { generativeModelLabel } from '@/lib/generativeModelLabel';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useHomepage } from '@/contexts/homepage-context';
import { useAuth } from '@/contexts/AuthContext';
import { GenerationGateModal } from '@/components/GenerationGateModal';
import { SpaceContent } from '@/components/featureTreeSidebar/SpaceContent';
import { LandContent } from '@/components/featureTreeSidebar/LandContent';
import { SpaceframeInlinePane } from '@/components/featureTreeSidebar/spaceframeWindows';
// Direct file import (not the `@/components/crosskart` barrel) — the barrel
// also re-exports the Cross Kart store singleton, which would eagerly pull
// docDefaults/catalog into this always-loaded sidebar (Lazy3D boundary).
import { CrossKartInlinePane } from '@/components/crosskart/crosskartWindows';
import {
  AirContent,
  SupinParamsSync,
} from '@/components/featureTreeSidebar/AirContent';
import { SeaContent } from '@/components/featureTreeSidebar/SeaContent';
import { ImplicitPrimitivesContent } from '@/components/featureTreeSidebar/ImplicitPrimitivesContent';
import { LatticeConverterContent } from '@/components/featureTreeSidebar/LatticeConverterContent';
import { ManualCadContent } from '@/components/featureTreeSidebar/ManualCadContent';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FeatureTreeWindowItems } from '@/components/featureTreeSidebar/FeatureTreeWindowItems';
import { UniversalDownloadMenu } from '@/components/download/UniversalDownloadMenu';
import { TubeframeParamsSync } from '@/components/featureTreeSidebar/TubeframeFields';
import { featureWindowItems } from '@/components/featureTreeSidebar/featureWindowRegistry';
import { resolveFeatureWindowSurface } from '@/components/featureTreeSidebar/featureWindowSurface';
import { useAutoOpenFirstFeatureWindow } from '@/components/featureTreeSidebar/useAutoOpenFirstFeatureWindow';

// ---------------------------------------------------------------------------
// Main sidebar component
// ---------------------------------------------------------------------------

export function FeatureTreeSidebar() {
  const {
    vehicleEnabled,
    vehicleTab,
    vehicleSubTab,
    textToCadSelected,
    cadMode,
    featureTreeOpen,
    setFeatureTreeOpen,
    model,
    setModel,
    outputFormat,
    setOutputFormat,
    type,
    sidebarGenerateEnabled,
    sidebarGenerateLoading,
    fireSidebarGenerate,
    fireSidebarStop,
    hasArtifact,
    regenEnabled,
    regenLoading,
    fireRegen,
    fireRegenStop,
    setDownloadSlot,
    setJawsSlot,
    implicitPrimitive,
  } = useHomepage();

  // Generation gating (#176): when blocked, the Generate button must not sit
  // silently disabled — a guest gets "sign up", a zero-credit user gets
  // "upgrade". usePromptSidebarActions already folds `limitReached` INTO
  // `sidebarGenerateEnabled` (enabled: … && !limitReached), so the sidebar
  // can't tell "blocked by params" from "blocked by credits" off that flag
  // alone. We recompute both signals here from auth — mirroring PromptView /
  // ParametricEditorView — to drive the gate instead of a dead control.
  const { user, billing, isLoading: authLoading } = useAuth();
  const isAnonymous = !user || user.is_anonymous;
  // Only a CONFIRMED zero balance blocks generation. billing === null while
  // not loading (e.g. billing-status invoke errored) fails OPEN — never
  // hard-block a funded user we just failed to price.
  const limitReached =
    !authLoading && billing != null && (billing.tokens.total ?? 0) <= 0;
  const [gateOpen, setGateOpen] = useState(false);

  const location = useLocation();
  const isEditorPage = location.pathname.startsWith('/editor/');

  // feature-tree-windows pilot (flag defaults OFF → prod unchanged). When ON AND
  // the active (vehicleTab, vehicleSubTab) has registered feature windows, the
  // domain's params render as JAWS-style tree items that open floating windows
  // in the viewer, instead of the legacy single pane. Only Land → Tubeframe
  // Joint is registered for now; every other surface falls through untouched.
  // Manual CAD (FreeCAD-style) mode — flag-gated (rule 7). When active, the
  // sidebar's parameter pane shows the manual feature tree instead of the
  // per-domain AI content. Flag OFF → forced Automatic → prod unchanged.
  const manualCadEnabled = useFeatureFlag('manual-cad', true);
  const isManualCad = manualCadEnabled && cadMode === 'manual';

  const featureTreeWindows = useFeatureFlag('feature-tree-windows', false);
  // The ONE resolver every windowing consumer (this sidebar, FeatureWindowHost,
  // the auto-open hook) agrees on — see featureWindowSurface.ts. Returns null
  // for surfaces with no registered windows (Manual CAD handled separately
  // above; the plain text-to-cad prompt box; disabled placeholder sub-tabs).
  const windowSurface = resolveFeatureWindowSurface({
    vehicleEnabled,
    vehicleTab,
    vehicleSubTab,
    textToCadSelected,
    implicitPrimitive,
  });
  const windowSurfaceItems = featureWindowItems(windowSurface?.key);
  const useWindowsForSurface =
    featureTreeWindows && windowSurfaceItems.length > 0;
  // "Select a feature → its first window opens" for every surface — reacts to
  // the resolved key changing, not to any one click handler.
  useAutoOpenFirstFeatureWindow(
    featureTreeWindows ? (windowSurface?.key ?? null) : null,
    windowSurfaceItems,
  );

  const isSpaceLocked =
    vehicleEnabled &&
    vehicleTab === 'space' &&
    vehicleSubTab === 'rocket-nozzle';

  // For the NASA SUPIN tab, the choice of LLM isn't user-relevant — geometry
  // comes from the Fortran solver. Hide it from the sidebar and rebrand the
  // header so the panel calls out the tool the user is actually configuring.
  const isSupinTab =
    vehicleEnabled &&
    vehicleTab === 'air' &&
    vehicleSubTab === 'nasa-supersonic-inlet';

  // Spaceframe Chassis is a deterministic CONFIGURATOR (per-class Python
  // templates, no LLM — docs/spaceframe/PLAN.md). Its workbench owns every
  // action (template/build/FEA/report/export), so the generative chrome — the
  // model/kernel pickers and the Generate button — would only mislead.
  const isSpaceframeTab =
    vehicleEnabled && vehicleTab === 'land' && vehicleSubTab === 'spaceframe';

  // Cross Kart (2 Seater) is the same kind of deterministic configurator as
  // Spaceframe Chassis (docs/spaceframe/crosskart/PLAN.md) — same reasoning,
  // same generative-chrome hiding.
  const isCrossKartTab =
    vehicleEnabled && vehicleTab === 'land' && vehicleSubTab === 'cross-kart';

  // Implicit Primitives tab lives under text-to-cad (not a vehicle domain).
  // Geometry comes from the ShapeKernel executor running hardcoded templates,
  // so the LLM is completely out of the loop AND the kernel choice is forced
  // to implicit. Both selectors get the "Proprietary" treatment; only the
  // kernel one gets a locked tooltip, since the kernel choice has a concrete
  // user-facing reason while the LLM choice is just hidden.
  const isPrimitivesTab =
    textToCadSelected && vehicleSubTab === 'implicit-primitives';

  // Land → Wheels uses the same implicit-primitive pipeline (rover-wheel is a
  // ShapeKernel composite). Same kernel lock + "Implicit Voxel LCEM" label
  // as the Primitives tab — only the parent domain differs.
  const isLandWheels =
    vehicleEnabled && vehicleTab === 'land' && vehicleSubTab === 'wheels';

  // Space → Heat Exchanger uses the same implicit-primitive pipeline (the helix
  // heat exchanger is a ShapeKernel composite, built at 0.5mm and served as
  // fidelity tiers). Same kernel lock + label as Land → Wheels.
  const isSpaceHeatExchanger =
    vehicleEnabled &&
    vehicleTab === 'space' &&
    vehicleSubTab === 'heat-exchanger';

  // NASA Lattice Primitives — sibling of Implicit Primitives, but driven by
  // the unitcellengine Python sidecar. Renders the same ImplicitPrimitivesContent
  // component (the if-else chain inside already handles `kind: 'nasa-lattice'`)
  // and locks the kernel to 'unitcell' instead of 'implicit'.
  const isNasaLatticeTab =
    textToCadSelected && vehicleSubTab === 'nasa-lattice-primitives';

  // NASA Unitcell Lattice Converter — uploads a part and fills it with a lattice
  // on the PicoGK kernel (epic #309). Shares the unitcell kernel lock + label
  // with NASA Lattice, but renders its own LatticeConverterContent panel (upload
  // + density sliders) rather than the single-cell ImplicitPrimitivesContent.
  const isUnitcellConverterTab =
    textToCadSelected && vehicleSubTab === 'nasa-unitcell-converter';

  // The plain Text-or-Image-to-CAD page: its prompt + Generate now live in the
  // centred PromptCard, so this sidebar renders an empty tree and no CTA. The
  // sub-tabs (converter / primitives / NASA lattice) are unaffected.
  const isPlainTextToCadPage =
    textToCadSelected &&
    !isUnitcellConverterTab &&
    !isPrimitivesTab &&
    !isNasaLatticeTab;
  const hidePromptCardDuplicateCta = isPlainTextToCadPage;

  // Tabs that drive an implicit primitive (whether from the Primitives library
  // or from a vehicle-domain wrapper like Land → Wheels). Treated identically
  // by the model/kernel selectors below. NASA lattice sits separately because
  // it forces a different kernel — kept out of this flag, see the dedicated
  // useEffect below.
  const isImplicitDriven =
    isPrimitivesTab || isLandWheels || isSpaceHeatExchanger;

  // The "no real LLM in the loop" UI treatment applies to both implicit and
  // NASA-lattice paths — different kernels, same "kernel-templated" posture.
  const isKernelDriven =
    isImplicitDriven || isNasaLatticeTab || isUnitcellConverterTab;

  // SUPIN gets a "NASA SUPIN" label; implicit-driven tabs get "Implicit Voxel
  // LCEM"; NASA-lattice + the Lattice Converter get "NASA Unitcell Engine". For
  // all of these the geometry does NOT come from the selected LLM, so the
  // dropdown is locked (greyed, but the label stays legible) and shares the same
  // snarky tooltip — see the `disabled` props on the selectors below.
  //
  // The label is derived from the SHARED generativeModelLabel helper (the same
  // one the chat bubble uses) so the live dropdown and the post-generation
  // bubble can never disagree. We feed it the kernel implied by the active tab
  // rather than the raw `outputFormat` state, because the locking useEffects run
  // a render late — on the first render after a tab switch `outputFormat` may
  // still hold the previous tab's value. `undefined` (normal LLM path) means no
  // override, so the dropdown shows the real selected model.
  const llmOverrideLabel =
    isSupinTab || isKernelDriven
      ? generativeModelLabel({
          outputFormat: isImplicitDriven
            ? 'implicit'
            : isNasaLatticeTab || isUnitcellConverterTab
              ? 'unitcell'
              : 'solidbody',
          hasSupinArtifact: isSupinTab,
        })
      : undefined;
  const showProprietaryTooltip = isSupinTab || isKernelDriven;
  const proprietaryTooltip = 'Need to know basis. Above your pay grade, buddy.';

  useEffect(() => {
    if (isSpaceLocked) {
      setOutputFormat('solidbody');
    }
  }, [isSpaceLocked, setOutputFormat]);

  // Primitives = implicit kernel only. Force the dropdown to implicit any time
  // the user is on this tab, so they can't accidentally desync the UI from
  // what the executor will actually produce. Same lock applies to Land →
  // Wheels, which shares the implicit-primitive pipeline.
  useEffect(() => {
    if (isImplicitDriven) {
      setOutputFormat('implicit');
    }
  }, [isImplicitDriven, setOutputFormat]);

  // NASA Lattice Primitives = unitcell kernel only. Same locking rationale as
  // above but a different kernel — the user-picked OutputFormatSelector value
  // is irrelevant for this path; the sub-tab dictates routing.
  useEffect(() => {
    if (isNasaLatticeTab || isUnitcellConverterTab) {
      setOutputFormat('unitcell');
    }
  }, [isNasaLatticeTab, isUnitcellConverterTab, setOutputFormat]);

  // NASA SUPIN renders server-side STL through the Solidbody (build123d) viewer
  // path, so lock the kernel to solidbody. Without this the dropdown shows
  // whatever was last selected (users were seeing a stale 'unitcell' label).
  // Routing for SUPIN is by vehicleSubtype, not outputFormat, so this is
  // display-only — it just keeps the label honest.
  useEffect(() => {
    if (isSupinTab) {
      setOutputFormat('solidbody');
    }
  }, [isSupinTab, setOutputFormat]);

  const models = useImageToCadModels();

  const [showRegenConfirm, setShowRegenConfirm] = useState(false);
  const [dontShowRegenAgain, setDontShowRegenAgain] = useState(false);

  const handleRegenClick = () => {
    if (sessionStorage.getItem('regen-confirm-skip') === 'true') {
      fireRegen();
    } else {
      setDontShowRegenAgain(false);
      setShowRegenConfirm(true);
    }
  };

  return (
    <>
      {/* Fixed sidebar — spans from below AppMenuBar to bottom of viewport */}
      <div
        className="fixed left-0 top-10 hidden lg:flex"
        style={{ bottom: 0, zIndex: 25 }}
      >
        {/* Content area — only rendered when open */}
        {featureTreeOpen && (
          <div className="flex w-64 flex-col overflow-hidden bg-telekinesis-bg-dark">
            {/* Generative Model + Physics Kernel selectors. On the NASA
                SUPIN tab the model dropdown trigger is blank — the model
                choice is irrelevant for SUPIN runs (the artifact comes from
                the Fortran solver, not an LLM). */}
            <div
              className={cn(
                'flex-shrink-0 space-y-3 border-b border-telekinesis-neutral-700 p-3',
                (isSpaceframeTab || isCrossKartTab) && 'hidden',
              )}
            >
              <div>
                <p className="mb-1 text-xs tracking-wide text-telekinesis-neutral-50">
                  Generative Model
                </p>
                <ModelSelector
                  models={models}
                  selectedModel={model}
                  onModelChange={setModel}
                  type={type}
                  className="w-full justify-between border border-telekinesis-neutral-700"
                  // Every non-LLM tab locks the model dropdown: SUPIN, the
                  // implicit-driven tabs (Primitives + Land → Wheels), and NASA
                  // Lattice. The geometry doesn't come from the selected LLM, so
                  // the dropdown is greyed — the displayLabel still reads legibly
                  // as the real source (e.g. "NASA SUPIN").
                  disabled={isKernelDriven || isSupinTab}
                  displayLabel={llmOverrideLabel}
                  lockedTooltip={
                    showProprietaryTooltip ? proprietaryTooltip : undefined
                  }
                />
              </div>
              <div>
                <p className="mb-1 text-xs text-telekinesis-neutral-50">
                  Physics Kernel
                </p>
                <OutputFormatSelector
                  selectedFormat={outputFormat}
                  onFormatChange={setOutputFormat}
                  useShortLabel={false}
                  className="w-full justify-between border border-telekinesis-neutral-700"
                  disabled={isSpaceLocked || isKernelDriven || isSupinTab}
                  // PHYSICS-KERNEL label override, Lattice Converter ONLY. The
                  // converter is locked to outputFormat 'unitcell' for routing
                  // (the unitcell-chat edge fn), but it computes on the PicoGK
                  // implicit voxel kernel — so the kernel label must read
                  // "Implicit Voxel Physics", not "NASA Unitcell Engine".
                  // NASA Lattice Primitives (isNasaLatticeTab, also 'unitcell')
                  // gets NO override — it really IS the unitcell engine.
                  displayLabel={
                    isUnitcellConverterTab
                      ? 'Implicit Voxel Physics'
                      : undefined
                  }
                  lockedTooltip={
                    isImplicitDriven
                      ? 'Implicit Physics Required'
                      : isUnitcellConverterTab
                        ? // Converter computes on PicoGK — its label reads
                          // "Implicit Voxel Physics", so the lock tooltip
                          // matches that, not the unitcell engine.
                          'Implicit Physics Required'
                        : isNasaLatticeTab
                          ? 'NASA Unitcell Engine Required'
                          : isSupinTab
                            ? 'Solidbody Physics is locked for NASA SUPIN'
                            : isSpaceLocked
                              ? 'Solidbody Physics is required for Python script defined structures'
                              : undefined
                  }
                />
              </div>
            </div>

            {/* Feature Tree label */}
            <div className="flex h-10 flex-shrink-0 items-center justify-center border-b border-telekinesis-neutral-700 px-3">
              <span className="text-sm text-telekinesis-neutral-50">
                Feature Tree
              </span>
            </div>

            {/* Generate / Regenerate 3D Model button. Hidden on the plain
                Text-or-Image-to-CAD page: the centred PromptCard owns that CTA
                there (it fires this same registered action), and two Generate
                buttons for one action is a worse page, not a safer one. */}
            <div
              className={cn(
                'flex-shrink-0 border-b border-telekinesis-neutral-700 p-3',
                (hidePromptCardDuplicateCta ||
                  isSpaceframeTab ||
                  isCrossKartTab) &&
                  'hidden',
              )}
            >
              {sidebarGenerateLoading ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={fireSidebarStop}
                      className="flex h-9 w-full items-center justify-center gap-2 rounded-md bg-telekinesis-gold text-sm font-semibold text-black transition-colors hover:bg-telekinesis-gold/80"
                    >
                      <Icosahedron className="h-4 w-4" />
                      <span className="h-3.5 w-3.5 rounded-[2px] bg-black" />
                      Stop Generation
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">Stop generation</TooltipContent>
                </Tooltip>
              ) : regenLoading ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={fireRegenStop}
                      className="flex h-9 w-full items-center justify-center gap-2 rounded-md bg-telekinesis-gold text-sm font-semibold text-black transition-colors hover:bg-telekinesis-gold/80"
                    >
                      <Icosahedron className="h-4 w-4" />
                      Cancel Generation
                      <span className="h-3.5 w-3.5 rounded-[2px] bg-black" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    Cancel generation
                  </TooltipContent>
                </Tooltip>
              ) : isEditorPage && hasArtifact ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="block w-full">
                      <button
                        onClick={handleRegenClick}
                        disabled={!regenEnabled}
                        className="flex h-9 w-full items-center justify-center gap-2 rounded-md bg-telekinesis-gold text-sm font-semibold text-black transition-colors hover:bg-telekinesis-gold/80 disabled:opacity-40 disabled:hover:bg-telekinesis-gold"
                      >
                        <Icosahedron className="h-4 w-4" />
                        Regenerate 3D Model
                        <RefreshCw className="h-4 w-4" />
                      </button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    {regenEnabled
                      ? 'Send it.'
                      : 'Change Parameters to Enable Regeneration'}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="block w-full">
                      {/* When blocked by credits/guest (#176) the button stays
                          CLICKABLE and opens the gate modal instead of firing
                          generate — never a silently-disabled control. The
                          params-not-ready disable is unchanged. */}
                      <button
                        onClick={
                          limitReached
                            ? () => setGateOpen(true)
                            : fireSidebarGenerate
                        }
                        disabled={!limitReached && !sidebarGenerateEnabled}
                        className="flex h-9 w-full items-center justify-center gap-2 rounded-md bg-telekinesis-gold text-sm font-semibold text-black transition-colors hover:bg-telekinesis-gold/80 disabled:opacity-40 disabled:hover:bg-telekinesis-gold"
                      >
                        <Icosahedron className="h-4 w-4" />
                        Generate 3D Model
                      </button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    {limitReached
                      ? isAnonymous
                        ? 'Sign up to generate.'
                        : 'Out of credits — upgrade to generate.'
                      : sidebarGenerateEnabled
                        ? 'Send it.'
                        : 'Change Parameters or enter Text Prompt to enable new generation.'}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>

            {/* Regenerate confirmation dialog */}
            <AlertDialog
              open={showRegenConfirm}
              onOpenChange={setShowRegenConfirm}
            >
              <AlertDialogContent>
                <AlertDialogCancel className="absolute right-4 top-4 mt-0 h-7 w-7 rounded-md border-0 bg-transparent p-0 text-telekinesis-neutral-400 opacity-70 hover:bg-telekinesis-neutral-700 hover:opacity-100 focus:ring-0">
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </AlertDialogCancel>
                <AlertDialogHeader className="text-center sm:text-center">
                  <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Regeneration costs more time &amp; tokens.
                    <br />
                    All generations will be saved to history in the parameter
                    chat.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <label className="flex cursor-pointer items-center justify-center gap-2 text-sm text-telekinesis-neutral-50">
                  <input
                    type="checkbox"
                    checked={dontShowRegenAgain}
                    onChange={(e) => setDontShowRegenAgain(e.target.checked)}
                    className="h-4 w-4 rounded accent-telekinesis-gold"
                  />
                  Don't show again
                </label>
                <AlertDialogFooter className="flex-row justify-center gap-3 sm:justify-center sm:space-x-0">
                  <AlertDialogAction
                    onClick={() => {
                      if (dontShowRegenAgain)
                        sessionStorage.setItem('regen-confirm-skip', 'true');
                      fireRegen();
                    }}
                  >
                    Yes
                  </AlertDialogAction>
                  <AlertDialogCancel className="mt-0">No</AlertDialogCancel>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {/* Generation gate (#176) — sign-up (guest) or upgrade (no
                credits) when the Generate button is blocked. */}
            <GenerationGateModal
              open={gateOpen}
              onOpenChange={setGateOpen}
              mode={isAnonymous ? 'guest' : 'no-credits'}
            />

            {/* Domain parameters — scrollable */}
            <div className="dark-scrollbar flex-1 overflow-y-auto p-3">
              {isManualCad ? (
                <ManualCadContent />
              ) : useWindowsForSurface && windowSurface ? (
                // Every domain's params render as SolidWorks-style feature-tree
                // items that open floating windows in the viewer
                // (FeatureWindowHost, mounted in the viewport). The domains
                // whose values are read independently of window open/close
                // (tubeframe's Generate gate, SUPIN's Generate payload) keep
                // their always-mounted sync alongside the tree — the fields are
                // one shared store either way, so this never desyncs from the
                // window content.
                <>
                  {windowSurface.key === 'land/tubeframe-joint' && (
                    <TubeframeParamsSync />
                  )}
                  {windowSurface.key === 'air/nasa-supersonic-inlet' && (
                    <SupinParamsSync />
                  )}
                  <FeatureTreeWindowItems
                    surfaceKey={windowSurface.key}
                    parentLabel={windowSurface.parentLabel}
                  />
                </>
              ) : textToCadSelected ? (
                isUnitcellConverterTab ? (
                  <LatticeConverterContent enabled />
                ) : isPrimitivesTab || isNasaLatticeTab ? (
                  <ImplicitPrimitivesContent enabled />
                ) : // Plain Text-or-Image-to-CAD: the tree renders EMPTY. The
                // prompt, the upload well and Generate all moved to the
                // centred PromptCard (PromptView), and Scan-to-CAD folded
                // into that card's "Mesh to CAD" tab. Domain navigation is
                // the top toolbar's DomainDropdown, so nothing is stranded
                // by an empty tree here. Mobile still renders the prompt
                // inline from PromptView and never sees this sidebar.
                null
              ) : !vehicleEnabled ? null : vehicleTab === 'space' ? (
                vehicleSubTab === 'heat-exchanger' ? (
                  <ImplicitPrimitivesContent enabled />
                ) : (
                  <SpaceContent enabled={vehicleEnabled} />
                )
              ) : vehicleTab === 'land' ? (
                vehicleSubTab === 'suspension-kinematics' ? (
                  // The JAWS workbench portals its controls (Import Vehicle,
                  // Type/Axle/…, feature tree, Download) INTO this slot, so JAWS
                  // lives in the shared feature tree instead of its own panel.
                  <div ref={setJawsSlot} />
                ) : vehicleSubTab === 'wheels' ? (
                  <ImplicitPrimitivesContent enabled />
                ) : vehicleSubTab === 'spaceframe' ? (
                  <SpaceframeInlinePane enabled={vehicleEnabled} />
                ) : vehicleSubTab === 'cross-kart' ? (
                  <CrossKartInlinePane enabled={vehicleEnabled} />
                ) : (
                  <LandContent enabled={vehicleEnabled} />
                )
              ) : vehicleTab === 'air' ? (
                <>
                  <SupinParamsSync />
                  <AirContent enabled={vehicleEnabled} />
                </>
              ) : vehicleTab === 'sea' ? (
                <SeaContent />
              ) : null}
            </div>
            {/* The ONE universal download button — bottom-left, at the bottom of
                the feature tree, white, always a menu. The active viewer just
                registers its file types (useRegisterDownload); this renders them.
                `ref={setDownloadSlot}` is the "sidebar footer host is mounted"
                signal viewers read to decide whether to float the same menu
                instead (collapsed tree / share view). Renders nothing when there's
                nothing to download, so `empty:hidden` collapses the footer. */}
            <div
              ref={setDownloadSlot}
              className="flex-shrink-0 border-t border-telekinesis-neutral-700 p-3 empty:hidden"
            >
              <UniversalDownloadMenu />
            </div>
          </div>
        )}

        {/* Clickable edge strip with centered arrow */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => setFeatureTreeOpen(!featureTreeOpen)}
              className="relative h-full w-3 flex-shrink-0 cursor-pointer border-r border-telekinesis-neutral-700 bg-telekinesis-bg-dark transition-colors hover:bg-telekinesis-gold/20"
              aria-label="Show/Hide Feature Tree"
            >
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-telekinesis-neutral-500">
                {featureTreeOpen ? (
                  <ChevronLeft className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
              </div>
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">Show/Hide Feature Tree</TooltipContent>
        </Tooltip>
      </div>

      {/* Spacer — keeps the flex layout correct while the sidebar is fixed */}
      <div
        className="hidden flex-shrink-0 lg:block"
        style={{ width: featureTreeOpen ? '16.75rem' : '0.75rem' }}
      />
    </>
  );
}
